import type { ClientMessage, ServerMessage } from "./types";

export interface RoomConnection {
  send: (msg: ClientMessage) => void;
  close: () => void;
}

export function connectRoom(
  roomCode: string,
  onMessage: (msg: ServerMessage) => void,
  onStatus: (status: "connecting" | "open" | "closed") => void
): RoomConnection {
  // In Discord, the activity loads through `https://<app-id>.discordsays.com/.proxy/`,
  // and the URL mappings in the dev portal route `/api` and `/ws` to the Worker.
  // Locally, Vite proxies `/api` and `/ws` to the Worker on :8787 (see vite.config.ts),
  // so the *path* is the same in both environments.
  const inDiscord = new URLSearchParams(location.search).has("frame_id");
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const path = inDiscord ? "/.proxy/ws" : "/ws";
  const target = `${proto}//${location.host}${path}?room=${encodeURIComponent(roomCode)}`;

  let ws: WebSocket | null = null;
  let closed = false;
  let backoff = 500;
  let pendingTimer: number | null = null;
  let reconnecting = false;

  const scheduleReconnect = () => {
    if (closed || reconnecting) return;
    reconnecting = true;
    onStatus("closed");
    backoff = Math.min(backoff * 2, 8000);
    pendingTimer = window.setTimeout(() => {
      pendingTimer = null;
      reconnecting = false;
      open();
    }, backoff);
  };

  const open = () => {
    if (closed) return;
    onStatus("connecting");
    let socket: WebSocket;
    try {
      socket = new WebSocket(target);
    } catch (err) {
      console.warn("ws ctor failed", err);
      scheduleReconnect();
      return;
    }
    ws = socket;

    socket.addEventListener("open", () => {
      if (ws !== socket) return;
      backoff = 500;
      onStatus("open");
    });
    socket.addEventListener("message", (evt) => {
      if (ws !== socket) return;
      try {
        onMessage(JSON.parse(evt.data) as ServerMessage);
      } catch {
        /* ignore malformed */
      }
    });
    // The browser fires `close` after `error`, so we only need one reconnect path.
    socket.addEventListener("close", () => {
      if (ws !== socket) return;
      ws = null;
      scheduleReconnect();
    });
    socket.addEventListener("error", () => {
      // No-op; let the close event drive reconnection so we don't double-trigger.
    });
  };

  open();

  return {
    send: (msg) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      }
    },
    close: () => {
      closed = true;
      if (pendingTimer !== null) {
        window.clearTimeout(pendingTimer);
        pendingTimer = null;
      }
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      ws = null;
    },
  };
}
